/*
  © 2024–2026 Atlas PHM
  Independently developed software. All rights reserved.
*/

/**
 * Cold Mains Data Selector
 * 
 * Provides a canonical way to read cold mains pressure and flow data,
 * handling both legacy and current field names, and determining certainty.
 * 
 * Handles backward compatibility with string values (from old persisted data)
 * and new number | null values (from current UI).
 */

import type { CollectedData } from './types';

export type ColdMainsCertainty = 'confirmed' | 'partial' | 'unknown';

export interface ColdMainsData {
  /** Whether a full pressure test was performed */
  hasFullTest: boolean;
  
  /** Static pressure in bar (null if not available) */
  staticPressureBar: number | null;
  
  /** Flow rate in L/min (null if not available) */
  flowLpm: number | null;
  
  /** Dynamic flow at 1 bar in L/min (null if not available) */
  dynamicFlowLpmAt1Bar: number | null;
  
  /** Dynamic pressure in bar (null if not available) */
  dynamicPressureBar: number | null;
  
  /** Cold main pipe type (e.g., "MDPE 25mm", "Copper 22mm", "Lead") */
  coldMainType: string | null;
  
  /** Certainty level based on data availability */
  certainty: ColdMainsCertainty;
  
  /** Whether values were user-provided or engine-assumed */
  source: 'user' | 'assumed';
}

/**
 * Parse a value that could be string | number | null | undefined into number | null
 * This handles backward compatibility with persisted string data.
 */
function parseToNumber(value: any): number | null {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  
  if (typeof value === 'number') {
    return isFinite(value) ? value : null;
  }
  
  if (typeof value === 'string') {
    const parsed = parseFloat(value.trim());
    return (isNaN(parsed) || !isFinite(parsed)) ? null : parsed;
  }
  
  return null;
}

/**
 * Select cold mains data from CollectedData, handling both legacy and current field names.
 * 
 * Priority order:
 * 1. New fields: mainsStaticPressureBar, mainsFlowLpm, mainsDynamicFlowLpm, mainsDynamicPressureBar
 * 2. Legacy fields: mainsPressure, flowRate
 * 
 * Certainty rules (Issue #4):
 * - "confirmed": staticPressureBar AND flowLpm both present AND valid
 * - "partial": Only one of the two required fields present
 * - "unknown": No information available at all
 * 
 * Note: Dynamic pair is optional - it provides bonus confidence but is NOT required for "confirmed" status.
 */
export function selectColdMains(input: CollectedData | null | undefined): ColdMainsData {
  if (!input) {
    return {
      hasFullTest: false,
      staticPressureBar: null,
      flowLpm: null,
      dynamicFlowLpmAt1Bar: null,
      dynamicPressureBar: null,
      coldMainType: null,
      certainty: 'unknown',
      source: 'assumed',
    };
  }

  // Read from new fields first, fall back to legacy fields
  // Use parseToNumber to handle both string (legacy) and number (current) types
  const staticPressureBar = 
    parseToNumber(input.mainsStaticPressureBar) ?? 
    parseToNumber(input.mainsPressure);

  const flowLpm = 
    parseToNumber(input.mainsFlowLpm) ?? 
    parseToNumber(input.flowRate);

  const dynamicFlowLpm = parseToNumber(input.mainsDynamicFlowLpm);
  const dynamicPressureBar = parseToNumber(input.mainsDynamicPressureBar);

  const coldMainType = input.coldMainType || null;

  // Determine if we have a full test (static + flow required, dynamic optional)
  const hasStaticAndFlow = staticPressureBar !== null && flowLpm !== null;
  const hasDynamic = dynamicFlowLpm !== null && dynamicPressureBar !== null;
  const hasFullTest = hasStaticAndFlow && hasDynamic;

  // Determine certainty per Issue #4 requirements
  let certainty: ColdMainsCertainty;
  let source: 'user' | 'assumed';

  if (hasStaticAndFlow) {
    // CONFIRMED: Both static pressure AND flow present & valid
    // Dynamic pair is bonus but NOT required for confirmed status
    certainty = 'confirmed';
    source = 'user';
  } else if (staticPressureBar === null && flowLpm === null) {
    // UNKNOWN: No data at all - this will be assumed by the engine
    certainty = 'unknown';
    source = 'assumed';
  } else {
    // PARTIAL: Only one of pressure or flow provided (incomplete data)
    certainty = 'partial';
    source = 'user'; // User provided partial data
  }

  return {
    hasFullTest,
    staticPressureBar,
    flowLpm,
    dynamicFlowLpmAt1Bar: dynamicFlowLpm,
    dynamicPressureBar,
    coldMainType,
    certainty,
    source,
  };
}

/**
 * Get effective values for use in calculations, applying defaults when needed.
 * This is separate from selectColdMains to make it clear when defaults are being used.
 */
export function getColdMainsWithDefaults(input: CollectedData | null | undefined): {
  staticPressureBar: number;
  flowLpm: number;
  dynamicFlowLpmAt1Bar: number | null;
  dynamicPressureBar: number | null;
} {
  const coldMains = selectColdMains(input);
  
  return {
    staticPressureBar: coldMains.staticPressureBar ?? 2.5, // Default assumption
    flowLpm: coldMains.flowLpm ?? 18, // Default assumption
    dynamicFlowLpmAt1Bar: coldMains.dynamicFlowLpmAt1Bar,
    dynamicPressureBar: coldMains.dynamicPressureBar,
  };
}
